import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError, fileUrl, formatPhone } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ResearchDialog from "@/components/ResearchDialog";
import RecordNav from "@/components/RecordNav";
import { ArrowLeft, Pencil, Mail, Phone, MapPin, GraduationCap, Briefcase, History, Star, User, Users2, Gift, CalendarCheck, CalendarClock, MessageSquare, Award, Heart, Sparkles, Share2, Linkedin, Facebook, Twitter, Instagram, Youtube, Github, Globe, ExternalLink, UserPlus, CheckCircle2, ShieldCheck, Tag, RotateCcw, Download, Trash2 } from "lucide-react";

const SOCIAL_META = [
  { key: "linkedin", label: "LinkedIn", icon: Linkedin },
  { key: "facebook", label: "Facebook", icon: Facebook },
  { key: "twitter", label: "X / Twitter", icon: Twitter },
  { key: "instagram", label: "Instagram", icon: Instagram },
  { key: "youtube", label: "YouTube", icon: Youtube },
  { key: "github", label: "GitHub", icon: Github },
  { key: "website", label: "Website", icon: Globe },
];

const TABS = [
  { id: "social", label: "Social", icon: Share2 },
  { id: "family", label: "Family", icon: Users2 },
  { id: "giving", label: "Giving", icon: Gift },
  { id: "events", label: "Events", icon: CalendarCheck },
  { id: "comms", label: "Comms", icon: MessageSquare },
  { id: "history", label: "History", icon: History },
];

const MEMBERSHIP_LABEL = { none: "Non-Member", annual: "Annual Member", lifetime: "Lifetime Member", honorary: "Honorary Member" };
const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0 })}`);

const Primary = () => <span className="inline-flex items-center gap-1 text-[10px] font-medium text-accent-foreground bg-accent/20 px-1.5 py-0.5 rounded"><Star className="h-3 w-3" /> Primary</span>;

const Row = ({ children, primary }) => (
  <div className="flex items-start justify-between gap-3 border border-border rounded-md p-3 bg-background">
    <div className="min-w-0">{children}</div>
    {primary && <Primary />}
  </div>
);

export default function Profile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const goBack = () => {
    try {
      const raw = sessionStorage.getItem("ud_record_return");
      if (raw) {
        const ctx = JSON.parse(raw);
        if (ctx && Array.isArray(ctx.ids) && ctx.ids.includes(id) && ctx.path) {
          sessionStorage.removeItem("ud_record_return");
          try { sessionStorage.removeItem("ud_nav_label"); } catch {}
          navigate(ctx.path);
          return;
        }
      }
    } catch {}
    navigate(-1);
  };
  const { isAdmin, isClassAgent, user } = useAuth();
  const emulated = !!user?.impersonated;
  const agentView = isClassAgent || emulated;
  const [navLabel] = useState(() => { try { return sessionStorage.getItem("ud_nav_label") || ""; } catch { return ""; } });
  const canEdit = isAdmin || (isClassAgent && !emulated);
  const dataBase = agentView ? "/agent/alumni" : "/alumni";
  const editBase = agentView ? "/agent/alumni" : "/alumni";
  const [a, setA] = useState(null);
  const [tab, setTab] = useState("social");
  const [research, setResearch] = useState(false);
  const [emailSendingOn, setEmailSendingOn] = useState(true);
  useEffect(() => { if (!isAdmin) return; api.get("/settings/email-sending").then((r) => setEmailSendingOn(r.data.sending_enabled)).catch(() => {}); }, [isAdmin]);

  const genClaimLink = async () => {
    try {
      const { data } = await api.post(`/alumni/${id}/claim-link`);
      if (data.claimed) { toast.info("This profile is already claimed."); return; }
      try {
        await navigator.clipboard.writeText(data.link);
        toast.success("Claim link copied to clipboard");
      } catch {
        toast.success("Claim link ready");
      }
      window.prompt("Share this claim link with the alumnus:", data.link);
    } catch (e) {
      toast.error("Could not generate claim link");
    }
  };

  const dsarExport = async () => {
    try {
      const r = await api.get(`/alumni/${id}/dsar-export`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `dsar-${(a?.last_name || id)}.json`;
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(url);
      toast.success("Data export downloaded");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not export this person's data");
    }
  };

  const dsarDelete = async () => {
    const name = a ? `${a.first_name || ""} ${a.last_name || ""}`.trim() : "this person";
    if (!window.confirm(`Permanently delete ALL data for ${name}? This erases their record, login, messages, group memberships, AI research, and photos. This cannot be undone.`)) return;
    if (!window.confirm("Are you absolutely sure? This is a permanent right-to-erasure deletion.")) return;
    try {
      await api.post(`/alumni/${id}/dsar-delete`, {});
      toast.success(`${name} and all associated data were permanently deleted.`);
      navigate(agentView ? "/agent" : "/alumni");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not delete this person's data");
    }
  };

  const unclaimProfile = async () => {
    if (!window.confirm("Unclaim this profile? This removes the linked login account so the record can be claimed again. For testing purposes.")) return;
    try {
      const { data } = await api.post(`/alumni/${id}/unclaim`);
      toast.success(data.message || "Profile reverted to unclaimed.");
      reload();
    } catch (e) {
      toast.error(apiError(e) || "Could not unclaim this profile");
    }
  };

  const claimedDate = (() => {
    const raw = a?.claimed_at || (a?.history || []).find((h) => h.action === "Profile claimed")?.at;
    if (!raw) return "";
    const d = new Date(raw);
    return isNaN(d) ? "" : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  })();

  useEffect(() => {
    api.get(`${dataBase}/${id}`).then((r) => setA(r.data)).catch(() => navigate(isClassAgent ? "/agent" : "/alumni"));
  }, [id, navigate]);

  const reload = async () => {
    try { const { data } = await api.get(`${dataBase}/${id}`); setA(data); } catch { /* noop */ }
  };

  const CHANNELS = [
    { v: "phone_call", label: "Phone call" },
    { v: "text", label: "Text message" },
    { v: "email", label: "Email" },
    { v: "voicemail", label: "Voicemail" },
    { v: "meeting", label: "Meeting" },
    { v: "letter", label: "Letter" },
    { v: "note", label: "Note" },
  ];
  const [logging, setLogging] = useState(false);
  const [savingLog, setSavingLog] = useState(false);
  const today = () => new Date().toISOString().slice(0, 10);
  const [logForm, setLogForm] = useState({ channel: "phone_call", date: today(), subject: "", notes: "" });

  const submitLog = async () => {
    if (!logForm.notes.trim() && !logForm.subject.trim()) { toast.error("Add a subject or notes for this communication."); return; }
    setSavingLog(true);
    try {
      await api.post(`/alumni/${id}/communications`, logForm);
      toast.success("Communication logged");
      setLogging(false);
      setLogForm({ channel: "phone_call", date: today(), subject: "", notes: "" });
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not log communication");
    } finally { setSavingLog(false); }
  };

  const deleteLog = async (commId) => {
    if (!window.confirm("Delete this logged communication?")) return;
    try { await api.delete(`/alumni/${id}/communications/${commId}`); await reload(); }
    catch { toast.error("Could not delete communication"); }
  };

  const toggleClassAgent = async () => {
    const enable = !a.is_class_agent;
    const msg = enable
      ? (a.claimed
          ? `Designate ${a.first_name || "this alumnus"} as Class Agent for the Class of ${a.class_year}? They'll be able to manage their class.`
          : `${a.first_name || "This alumnus"} hasn't claimed their profile yet. Designate them as Class Agent for the Class of ${a.class_year}? Their access activates automatically when they claim their profile.`)
      : "Remove Class Agent access for this alumnus?";
    if (!window.confirm(msg)) return;
    try {
      const { data: r } = await api.post(`/alumni/${id}/class-agent`, { enabled: enable });
      toast.success(enable ? (r?.pending_claim ? "Class Agent designated — activates when they claim" : "Class Agent designated") : "Class Agent access removed");
      const { data } = await api.get(`/alumni/${id}`);
      setA(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update Class Agent status");
    }
  };

  if (!a) return <div className="text-muted-foreground">Loading…</div>;

  const name = [(a.preferred_name || a.first_name), a.last_name, a.suffix].filter(Boolean).join(" ");
  const legalName = [a.first_name, a.middle_name, a.last_name, a.suffix].filter(Boolean).join(" ");
  const primaryEmail = a.emails?.find((x) => x.primary) || a.emails?.[0];
  const primaryPhone = a.phones?.find((x) => x.primary) || a.phones?.[0];
  const primaryAddress = a.addresses?.find((x) => x.primary) || a.addresses?.[0];
  const solicit = (a.solicit_code || "").toLowerCase();
  const prefs = a.email_prefs || {};
  const prefList = [["Events", prefs.events !== false], ["Announcements", prefs.announcements !== false], ["Fundraising", prefs.solicitations !== false]];
  const allOff = prefList.every(([, on]) => !on);
  const reallyOptedOut = !!a.self_unsubscribed || allOff || /unsubscribe|opt out|no email|do not email/.test(solicit);
  const doNotCall = /do not call/.test(solicit);
  const doNotMail = /do not mail/.test(solicit);
  const optOutHist = (a.history || []).filter((h) => /opt(ed)? out|unsubscrib/i.test(h.action || "")).map((h) => h.at).filter(Boolean).sort();
  const optOutDate = a.opt_out_date || (optOutHist.length ? optOutHist[optOutHist.length - 1] : null);

  return (
    <div className="fade-up">
      <div className="flex items-center gap-3 mb-5">
        <div className="flex-1 flex items-center gap-3 min-w-0">
          <button data-testid="profile-back" onClick={goBack} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          {agentView && navLabel && (
            <div data-testid="agent-view-context" className="hidden sm:flex items-center gap-2 text-sm">
              <ShieldCheck className="h-4 w-4 text-primary" />
              <span className="text-muted-foreground">Class Agent view ·</span>
              <span className="font-medium">{navLabel}</span>
            </div>
          )}
        </div>
        {(isAdmin || agentView) && (
          <div className="flex justify-center shrink-0">
            <RecordNav id={id} mode="view" basePath={dataBase} />
          </div>
        )}
        <div className="flex-1" />
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          <div className="space-y-6">
          <div className="bg-card border border-border rounded-lg shadow-sm p-6 text-center">
            {a.photo ? (
              <img src={fileUrl(a.photo)} alt={name} className="h-28 w-28 rounded-full object-cover mx-auto ring-4 ring-muted" />
            ) : (
              <div className="h-28 w-28 rounded-full bg-secondary/10 flex items-center justify-center mx-auto text-3xl font-serif text-secondary">{(a.first_name || "?").slice(0, 1)}{(a.last_name || "").slice(0, 1)}</div>
            )}
            <h1 className="font-serif text-2xl tracking-tight mt-4">{name.trim() || "Unnamed"}</h1>
            {a.former_name ? <div className="text-xs text-muted-foreground h-4 leading-4">née {a.former_name}</div> : <div className="h-4" aria-hidden="true" />}
            {a.middle_name && legalName !== name && <div className="text-xs text-muted-foreground">{legalName}</div>}
            <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
              <span className="text-sm font-medium text-primary">Class of {a.class_year ?? "—"}</span>
              <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${a.living_status === "living" ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}`}>
                {a.living_status === "living" ? "Living" : "Deceased"}
              </span>
              {a.membership_status && a.membership_status !== "none" && (
                <span data-testid="membership-badge" className="px-2 py-0.5 rounded text-[11px] font-medium bg-accent/20 text-accent-foreground">{MEMBERSHIP_LABEL[a.membership_status]}</span>
              )}
            </div>
            {(isAdmin || agentView) && (
              <button data-testid="profile-edit-button" onClick={() => navigate(`${editBase}/${id}/edit`)} className="mt-5 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors">
                <Pencil className="h-4 w-4" /> Edit Profile
              </button>
            )}
            {isAdmin && (
              <button data-testid="profile-class-agent-button" onClick={toggleClassAgent} className={`mt-2 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium border transition-colors ${a.is_class_agent ? "bg-accent/20 border-accent text-accent-foreground hover:bg-accent/30" : "border-border text-foreground hover:bg-muted"}`}>
                <ShieldCheck className="h-4 w-4" /> {a.is_class_agent ? "Remove Class Agent" : "Make Class Agent"}
              </button>
            )}
            {(isAdmin || agentView) && (
              <button data-testid="profile-research-button" onClick={() => setResearch(true)} className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md border border-primary text-primary text-sm font-medium hover:bg-primary/5 transition-colors">
                <Sparkles className="h-4 w-4" /> Research Alumnus
              </button>
            )}
            {isAdmin && (
              a.claimed ? (
                <>
                  <div data-testid="profile-claimed-badge" className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-emerald-50 text-emerald-800 text-sm font-medium border border-emerald-200">
                    <CheckCircle2 className="h-4 w-4" /> Profile Claimed{claimedDate ? <span className="font-normal text-emerald-700/80">· {claimedDate}</span> : null}
                  </div>
                  <button data-testid="profile-unclaim-button" onClick={unclaimProfile} className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-border text-muted-foreground text-xs font-medium hover:bg-muted hover:text-foreground transition-colors">
                    <RotateCcw className="h-3.5 w-3.5" /> Unclaim (testing)
                  </button>
                </>
              ) : (
                <button data-testid="profile-claim-button" onClick={genClaimLink} className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md border border-accent text-accent-foreground bg-accent/10 text-sm font-medium hover:bg-accent/20 transition-colors">
                  <UserPlus className="h-4 w-4" /> Copy "Claim Profile" Link
                </button>
              )
            )}
            {isAdmin && (
              <div data-testid="dsar-actions" className="mt-4 pt-4 border-t border-border">
                <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground mb-2">Data privacy (DSAR)</div>
                <button data-testid="dsar-export-button" onClick={dsarExport} className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-border text-foreground text-xs font-medium hover:bg-muted transition-colors">
                  <Download className="h-3.5 w-3.5" /> Export this person's data
                </button>
                <button data-testid="dsar-delete-button" onClick={dsarDelete} className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-destructive/50 text-destructive text-xs font-medium hover:bg-destructive/10 transition-colors">
                  <Trash2 className="h-3.5 w-3.5" /> Delete &amp; forget this person
                </button>
              </div>
            )}
            {isAdmin && (() => {
              const kw = /email|subscri|opt/i;
              const consent = (a.history || []).filter((h) => kw.test(h.action || "")).slice().reverse();
              return (
                <div data-testid="consent-history" className="mt-4 pt-4 border-t border-border">
                  <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground mb-2">Consent history</div>
                  {consent.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">No consent changes on record.</p>
                  ) : (
                    <ul className="space-y-1.5 max-h-40 overflow-y-auto text-left">
                      {consent.map((h, i) => {
                        const optOut = /opted out|unsubscribed/i.test(h.action || "");
                        return (
                          <li key={h.id || i} data-testid="consent-row" className="flex items-start gap-2 text-xs">
                            <span className={`mt-1 h-1.5 w-1.5 rounded-full shrink-0 ${optOut ? "bg-rose-500" : "bg-emerald-500"}`} />
                            <span className="text-foreground">{h.action}
                              <span className="text-muted-foreground"> · {h.at ? new Date(h.at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—"}{h.by_name || h.by ? ` · ${h.by_name || h.by}` : ""}</span>
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })()}
            {a.bio && <p className="mt-4 text-sm text-muted-foreground text-left leading-relaxed">{a.bio}</p>}
          </div>

          <div className="bg-card border border-border rounded-lg shadow-sm p-5 text-sm space-y-2 min-h-[196px]">
            <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Quick Facts</div>
            <div className="flex items-center gap-2"><Award className="h-4 w-4 text-muted-foreground" /> {MEMBERSHIP_LABEL[a.membership_status] || "Non-Member"}</div>
            <div className="flex items-center gap-2"><Gift className="h-4 w-4 text-muted-foreground" /> {money((a.giving || []).reduce((s, g) => s + (g.amount || 0), 0))} lifetime giving</div>
            {a.birthdate && <div className="flex items-center gap-2" data-testid="quickfact-birthdate"><CalendarClock className="h-4 w-4 text-muted-foreground" /> Born {(() => { const d = new Date(a.birthdate); return isNaN(d) ? a.birthdate : d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" }); })()}</div>}
            {a.last_gift_date && <div className="flex items-center gap-2" data-testid="quickfact-last-gift"><CalendarClock className="h-4 w-4 text-muted-foreground" /> Last gift {a.last_gift_date}</div>}
            <div className="flex items-center gap-2"><CalendarCheck className="h-4 w-4 text-muted-foreground" /> {a.events?.length || 0} events attended</div>
            <div className="flex items-center gap-2"><User className="h-4 w-4 text-muted-foreground" /> {a.emails?.length || 0} emails · {a.phones?.length || 0} phones</div>
            <div className="flex items-center gap-2"><Briefcase className="h-4 w-4 text-muted-foreground" /> {a.employments?.length || 0} employment records</div>
            {a.solicit_code && <div className="flex items-start gap-2" data-testid="quickfact-solicit"><Tag className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" /> <span><span className="text-xs font-mono uppercase tracking-wide text-muted-foreground">Solicit code:</span> {a.solicit_code}</span></div>}
            {a.interests?.length > 0 && (
              <div className="pt-2">
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1.5">Interests</div>
                <div className="flex flex-wrap gap-1.5">
                  {a.interests.map((it, i) => <span key={i} data-testid="interest-chip" className="px-2 py-0.5 rounded-full bg-accent/20 text-accent-foreground text-xs">{it}</span>)}
                </div>
              </div>
            )}
          </div>
          </div>

          <div className="space-y-6">
            {a.engagement && (
              <div data-testid="engagement-card" className="bg-card border border-border rounded-lg shadow-sm p-5">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Engagement</div>
                  <span data-testid="engagement-tier" className={`px-2 py-0.5 rounded text-[11px] font-semibold ${a.engagement.score >= 70 ? "bg-emerald-100 text-emerald-800" : a.engagement.score >= 40 ? "bg-blue-100 text-blue-800" : a.engagement.score >= 15 ? "bg-amber-100 text-amber-800" : "bg-slate-200 text-slate-700"}`}>{a.engagement.tier}</span>
                </div>
                <div className="flex items-end gap-2">
                  <div data-testid="engagement-score" className="text-3xl font-serif text-primary leading-none">{a.engagement.score}</div>
                  <div className="text-xs text-muted-foreground mb-1">engagement score</div>
                </div>
                <div className="grid grid-cols-4 gap-2 mt-3 text-center">
                  {[["Gifts", a.engagement.gifts], ["Events", a.engagement.events], ["Opens", a.engagement.opens], ["Activity", a.engagement.activity]].map(([label, val]) => (
                    <div key={label}><div className="text-sm font-semibold">{val ?? 0}</div><div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div></div>
                  ))}
                </div>
              </div>
            )}

            <div data-testid="contact-badge" className="bg-card border border-border rounded-lg shadow-sm p-5 min-h-[184px]">
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">Contact</div>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-5">
                <div className="space-y-2.5 text-sm">
                  <div className="flex items-start gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                    {a.emails?.length ? (
                      <div className="space-y-1 min-w-0" data-testid="contact-emails">
                        {[...a.emails].sort((x, y) => (y.primary ? 1 : 0) - (x.primary ? 1 : 0)).map((e, i) => (
                          <div key={i} className="flex items-center gap-2 flex-wrap">
                            <a href={`mailto:${e.value}`} className="text-primary hover:underline break-all">{e.value}</a>
                            {e.primary && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-primary/10 text-primary uppercase tracking-wide">Primary</span>}
                            {e.type && <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{e.type}</span>}
                          </div>
                        ))}
                      </div>
                    ) : <span className="text-muted-foreground">No email on file</span>}
                  </div>
                  {a.phones?.length ? (
                    <div className="flex items-start gap-2" data-testid="contact-phones">
                      <Phone className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                      <div className="space-y-1 min-w-0">
                        {[...a.phones].sort((x, y) => (y.primary ? 1 : 0) - (x.primary ? 1 : 0)).map((p, i) => (
                          <div key={i} className="flex items-center gap-2 flex-wrap">
                            <span>{formatPhone(p.value)}</span>
                            {p.primary && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-primary/10 text-primary uppercase tracking-wide">Primary</span>}
                            {p.type && <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{p.type}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground shrink-0" /> <span className="text-muted-foreground">No phone on file</span></div>}
                  {a.addresses?.length ? (
                    <div className="flex items-start gap-2" data-testid="contact-addresses">
                      <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                      <div className="space-y-2 min-w-0">
                        {[...a.addresses].sort((x, y) => (y.primary ? 1 : 0) - (x.primary ? 1 : 0)).map((ad, i) => (
                          <div key={i}>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span>{[ad.line1, ad.line2].filter(Boolean).join(", ") || "—"}</span>
                              {ad.primary && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-primary/10 text-primary uppercase tracking-wide">Primary</span>}
                            </div>
                            <span className="block text-muted-foreground">{[[ad.city, ad.state].filter(Boolean).join(", "), ad.postal_code, ad.country].filter(Boolean).join(" · ")}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : <div className="flex items-start gap-2"><MapPin className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" /> <span className="text-muted-foreground">No address on file</span></div>}
                </div>
                <div className="space-y-4 text-sm sm:border-l sm:border-border sm:pl-6">
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Solicit Code</div>
                    <div className="flex items-center gap-2" data-testid="contact-solicit"><Tag className="h-4 w-4 text-muted-foreground shrink-0" /> {a.solicit_code ? a.solicit_code : <span className="text-muted-foreground">—</span>}</div>
                  </div>
                  {!emailSendingOn && (
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Email Sending</div>
                      <span data-testid="email-off-badge" className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-amber-100 text-amber-800">Email OFF — system-wide</span>
                    </div>
                  )}
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">Opt-out Status</div>
                    {reallyOptedOut ? (
                      <span data-testid="pref-opted-out" className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-red-100 text-red-800">Opted out of all email</span>
                    ) : (
                      <div className="flex flex-wrap gap-1.5" data-testid="pref-chips">
                        {prefList.map(([label, on]) => (
                          <span key={label} className={`px-2 py-0.5 rounded text-[11px] font-medium ${on ? "bg-emerald-100 text-emerald-800" : "bg-red-50 text-red-700 line-through"}`}>{label}</span>
                        ))}
                      </div>
                    )}
                    {(doNotCall || doNotMail) && (
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {doNotCall && <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-slate-100 text-slate-600">Do Not Call</span>}
                        {doNotMail && <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-slate-100 text-slate-600">Do Not Mail</span>}
                      </div>
                    )}
                    {a.self_unsubscribed && <div className="text-[11px] text-amber-700 mt-1.5">Self-unsubscribed</div>}
                    {(reallyOptedOut || prefList.some(([, on]) => !on) || doNotCall || doNotMail) && (
                      <div className="text-[11px] text-muted-foreground mt-1.5" data-testid="optout-date">
                        Opted out: {optOutDate ? new Date(optOutDate).toLocaleDateString() : <span className="text-amber-700 font-medium">Unknown</span>}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div data-testid="education-badge" className="bg-card border border-border rounded-lg shadow-sm p-5 min-h-[116px]">
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">Education</div>
              {a.degrees?.length ? (
                <div className="space-y-2.5 text-sm">
                  {a.degrees.map((d) => (
                    <div key={d.id} className="flex items-start gap-2">
                      <GraduationCap className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                      <div>
                        <div className="font-medium">{d.degree || "Degree"}{d.grad_year ? ` · ${d.grad_year}` : ""}</div>
                        <div className="text-muted-foreground">{[d.major && `Major: ${d.major}`, d.minor && `Minor: ${d.minor}`].filter(Boolean).join(" · ") || "—"}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <div className="text-sm text-muted-foreground">No degrees on file.</div>}
            </div>

            <div data-testid="employment-badge" className="bg-card border border-border rounded-lg shadow-sm p-5 min-h-[116px]">
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">Employment</div>
              {a.employments?.length ? (
                <div className="space-y-2.5 text-sm">
                  {a.employments.map((emp) => (
                    <div key={emp.id} className="flex items-start gap-2">
                      <Briefcase className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                      <div>
                        <div className="font-medium">{emp.title || "Role"}</div>
                        <div className="text-muted-foreground">{[emp.employer, emp.industry].filter(Boolean).join(" · ") || "—"}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <div className="text-sm text-muted-foreground">No employment on file.</div>}
            </div>
          </div>
        </div>

        <div>
          <div className="bg-card border border-border rounded-lg shadow-sm">
            <div className="flex flex-wrap border-b border-border">
              {TABS.map((t) => {
                const Icon = t.icon;
                return (
                  <button
                    key={t.id}
                    data-testid={`tab-${t.id}`}
                    onClick={() => setTab(t.id)}
                    className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${tab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                  >
                    <Icon className="h-4 w-4" /> {t.label}
                  </button>
                );
              })}
            </div>

            <div className="p-5 space-y-6">
              {tab === "family" && (
                <Section title="Family & Relationships" empty={!a.family?.length}>
                  {a.family?.map((m) => (
                    <Row key={m.id}>
                      <div className="flex items-center gap-2"><Heart className="h-4 w-4 text-muted-foreground" /> <span className="font-medium">{m.name || "—"}</span><span className="text-xs text-muted-foreground">· {m.relationship}</span></div>
                      {m.notes && <div className="text-sm text-muted-foreground mt-1">{m.notes}</div>}
                    </Row>
                  ))}
                </Section>
              )}
              {tab === "giving" && (
                <>
                  <div className="flex items-center justify-between border border-border rounded-md p-3 bg-muted/40 mb-2">
                    <span className="text-sm font-medium">Lifetime Giving</span>
                    <span data-testid="lifetime-giving" className="font-serif text-xl">{money((a.giving || []).reduce((s, g) => s + (g.amount || 0), 0))}</span>
                  </div>
                  <Section title="Gifts & Pledges" empty={!a.giving?.length}>
                    {a.giving?.map((g) => (
                      <Row key={g.id}>
                        <div className="flex items-center gap-2"><Gift className="h-4 w-4 text-muted-foreground" /> <span className="font-medium">{money(g.amount)}</span><span className="text-xs text-muted-foreground">{g.date}</span></div>
                        <div className="text-sm text-muted-foreground">{[g.campaign, g.fund].filter(Boolean).join(" · ") || "—"}</div>
                      </Row>
                    ))}
                  </Section>
                </>
              )}
              {tab === "events" && (
                <Section title="Event Attendance" empty={!a.events?.length}>
                  {a.events?.map((ev) => (
                    <Row key={ev.id}>
                      <div className="flex items-center gap-2"><CalendarCheck className="h-4 w-4 text-muted-foreground" /> <span className="font-medium">{ev.event_name || "—"}</span></div>
                      <div className="text-sm text-muted-foreground">{[ev.date, ev.role].filter(Boolean).join(" · ") || "—"}</div>
                    </Row>
                  ))}
                </Section>
              )}
              {tab === "comms" && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Communication History</div>
                    {isAdmin && !logging && (
                      <button data-testid="log-comm-button" onClick={() => { setLogForm({ channel: "phone_call", date: today(), subject: "", notes: "" }); setLogging(true); }}
                        className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-primary text-white font-medium hover:bg-primary/90 transition-colors">
                        <UserPlus className="h-3.5 w-3.5" /> Log communication
                      </button>
                    )}
                  </div>

                  {logging && (
                    <div data-testid="log-comm-form" className="border border-border rounded-lg p-4 bg-background space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-muted-foreground mb-1 block">Type</label>
                          <select data-testid="log-comm-channel" value={logForm.channel} onChange={(e) => setLogForm((f) => ({ ...f, channel: e.target.value }))} className="ud-input w-full">
                            {CHANNELS.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground mb-1 block">Date</label>
                          <input data-testid="log-comm-date" type="date" value={logForm.date} onChange={(e) => setLogForm((f) => ({ ...f, date: e.target.value }))} className="ud-input w-full" />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block">Subject</label>
                        <input data-testid="log-comm-subject" value={logForm.subject} onChange={(e) => setLogForm((f) => ({ ...f, subject: e.target.value }))} placeholder="e.g. Reunion follow-up call" className="ud-input w-full" />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block">Notes</label>
                        <textarea data-testid="log-comm-notes" value={logForm.notes} onChange={(e) => setLogForm((f) => ({ ...f, notes: e.target.value }))} rows={3} placeholder="What was discussed?" className="ud-input w-full resize-y" />
                      </div>
                      <div className="flex items-center gap-2 justify-end">
                        <button data-testid="log-comm-cancel" onClick={() => setLogging(false)} className="px-4 py-2 rounded-md border border-border text-sm hover:bg-muted">Cancel</button>
                        <button data-testid="log-comm-save" disabled={savingLog} onClick={submitLog} className="px-4 py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-60">{savingLog ? "Saving…" : "Save"}</button>
                      </div>
                    </div>
                  )}

                  {(!a.communications?.length) ? (
                    <div className="text-sm text-muted-foreground border border-dashed border-border rounded-md p-3">None on file.</div>
                  ) : (
                    <div className="space-y-2">
                      {[...a.communications].reverse().map((c) => (
                        <div key={c.id} data-testid={`comm-row-${c.id}`} className="flex items-start justify-between gap-3 border border-border rounded-md p-3 bg-background">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <MessageSquare className="h-4 w-4 text-muted-foreground shrink-0" />
                              <span className="font-medium capitalize">{(c.channel || "note").replace(/_/g, " ")}</span>
                              {c.date && <span className="text-xs text-muted-foreground">· {c.date}</span>}
                              {c.logged_by && <span className="text-xs text-muted-foreground">· by {c.logged_by}</span>}
                            </div>
                            {c.subject && <div className="text-sm font-medium mt-1">{c.subject}</div>}
                            {c.notes && <div className="text-sm text-muted-foreground mt-0.5 whitespace-pre-wrap">{c.notes}</div>}
                          </div>
                          {isAdmin && (
                            <button data-testid={`comm-delete-${c.id}`} onClick={() => deleteLog(c.id)} title="Delete" className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-destructive shrink-0">
                              <span className="sr-only">Delete</span>
                              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {tab === "social" && (
                <Section title="Social Media" empty={!a.social || !SOCIAL_META.some((p) => a.social[p.key])}>
                  {SOCIAL_META.filter((p) => a.social && a.social[p.key]).map((p) => {
                    const Icon = p.icon;
                    const url = a.social[p.key];
                    const href = url.startsWith("http") ? url : `https://${url}`;
                    return (
                      <a key={p.key} data-testid={`social-link-${p.key}`} href={href} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between gap-3 border border-border rounded-md p-3 bg-background hover:border-primary hover:bg-primary/5 transition-colors">
                        <div className="flex items-center gap-2 min-w-0">
                          <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="font-medium">{p.label}</span>
                          <span className="text-sm text-muted-foreground truncate">{url}</span>
                        </div>
                        <ExternalLink className="h-4 w-4 text-muted-foreground shrink-0" />
                      </a>
                    );
                  })}
                </Section>
              )}
              {tab === "history" && (
                <div className="relative pl-6">
                  <div className="absolute left-1.5 top-1 bottom-1 w-px bg-border" />
                  {(a.history || []).slice().reverse().map((h) => (
                    <div key={h.id} data-testid="history-event" className="relative mb-5">
                      <div className="absolute -left-[18px] top-1 h-3 w-3 rounded-full bg-primary ring-4 ring-card" />
                      <div className="text-sm font-medium">{h.action}{h.detail ? `: ${h.detail}` : ""}</div>
                      <div className="text-xs text-muted-foreground">{h.by_name || h.by} · {new Date(h.at).toLocaleString()}</div>
                    </div>
                  ))}
                  {(!a.history || a.history.length === 0) && <div className="text-muted-foreground text-sm">No history recorded.</div>}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {research && <ResearchDialog alumnus={a} onClose={() => setResearch(false)} onApplied={(updated) => setA(updated)} putBase={dataBase} />}
    </div>
  );
}

const Section = ({ title, children, empty }) => (
  <div>
    <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">{title}</div>
    {empty ? <div className="text-sm text-muted-foreground border border-dashed border-border rounded-md p-3">None on file.</div> : <div className="space-y-2">{children}</div>}
  </div>
);
